import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { apiProxyRequest } from "./request";
import { AUTH_ENDPOINTS } from "./auth-endpoints";
import type { AuthBootstrapStatus } from "./auth-bootstrap";
import { logger } from "@noiman/shared-utils";

export interface UserLevelInfo {
  level: number;
  rank: string;
  totalXp: number;
  xpToNextLevel: number;
  progressPercent: number;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  levelInfo?: UserLevelInfo;
}

export interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;
  refreshAuth: () => Promise<User | null>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const LOG_CONTEXT = { scope: "AuthProvider" } as const;
// focus / visibilitychange で /me を取り直す最短の間隔。数える起点は、
// 理由を問わず最後に /me を取りに行った時刻 (画面の読み込み時も含む)。
// 5 秒だと読み込み直後の focus で同じ /me をもう一度取っていた。
// 役割の変更や退会は api-go が要求ごとに判定するので、ここが遅れても
// 画面の表示が遅れるだけ。同じブラウザの別タブでのログインとログアウトは
// AUTH_CHANNEL_NAME で即座に伝えるので、この間隔を待たない
const FOCUS_SYNC_RATE_LIMIT_MS = 60_000;
const AUTH_CHANNEL_NAME = "yumesaku-auth";

type AuthChannelMessage = { userId: string | null };

interface AuthProviderProps {
  children: React.ReactNode;
  bootstrapStatus?: AuthBootstrapStatus;
}

export function AuthProvider({ children, bootstrapStatus }: AuthProviderProps) {
  const resolvedBootstrapStatus = bootstrapStatus ?? "unknown";
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(
    resolvedBootstrapStatus !== "anonymous",
  );
  const authGenerationRef = useRef(0);
  const sessionRequestRef = useRef<Promise<User | null> | null>(null);
  const lastSessionCheckAtRef = useRef(Number.NEGATIVE_INFINITY);
  // 別タブからの知らせと比べる値。描画を待たずに読めるよう、setUser と同時に書く
  const userIdRef = useRef<string | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const publishUser = useCallback((nextUser: User | null) => {
    userIdRef.current = nextUser?.id ?? null;
    setUser(nextUser);
  }, []);

  const announceUser = useCallback((userId: string | null) => {
    try {
      channelRef.current?.postMessage({ userId } satisfies AuthChannelMessage);
    } catch {
      // 閉じた channel への送信は捨てる。次の focus で拾える
    }
  }, []);

  const loadCurrentSession = useCallback((): Promise<User | null> => {
    if (sessionRequestRef.current) return sessionRequestRef.current;

    const generation = authGenerationRef.current;
    lastSessionCheckAtRef.current = Date.now();
    const request = (async () => {
      try {
        logger.debug("fetching current session", LOG_CONTEXT);
        const result = await apiProxyRequest.get(null, AUTH_ENDPOINTS.ME);
        if (!result.isErr && result.data) {
          const data = result.data as { user: User };
          if (generation !== authGenerationRef.current) return null;
          logger.info("session restored", {
            ...LOG_CONTEXT,
            userId: data.user.id,
          });
          publishUser(data.user);
          return data.user;
        } else {
          if (generation === authGenerationRef.current) {
            logger.warn("no active session", LOG_CONTEXT);
            publishUser(null);
          }
          return null;
        }
      } catch (error) {
        if (generation === authGenerationRef.current) {
          logger.error("session fetch failed", {
            ...LOG_CONTEXT,
            error,
          });
          publishUser(null);
        }
        return null;
      }
    })();

    sessionRequestRef.current = request;
    void request.finally(() => {
      if (sessionRequestRef.current === request) {
        sessionRequestRef.current = null;
      }
    });
    return request;
  }, [publishUser]);

  useEffect(() => {
    if (resolvedBootstrapStatus === "anonymous") {
      // The server proved this navigation carried no accepted credential.
      // Invalidate an older in-flight lookup before publishing logged-out.
      authGenerationRef.current += 1;
      sessionRequestRef.current = null;
      publishUser(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const generation = authGenerationRef.current;
    void loadCurrentSession()
      .then((loaded) => {
        // 別タブでログインして開いた画面なら、ほかのタブにも知らせる
        if (loaded) announceUser(loaded.id);
      })
      .finally(() => {
        if (generation === authGenerationRef.current) setIsLoading(false);
      });
  }, [resolvedBootstrapStatus, loadCurrentSession, announceUser, publishUser]);

  useEffect(() => {
    // Other consumers of the shared provider did not opt into the server
    // bootstrap contract; preserve their prior mount-only behavior.
    if (bootstrapStatus === undefined) return;

    const syncAfterPossibleCrossTabChange = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - lastSessionCheckAtRef.current < FOCUS_SYNC_RATE_LIMIT_MS)
        return;
      void loadCurrentSession();
    };

    // 受け取った側は取り直すだけで、結果を送り返さない (往復しない)。
    // 中身は信じず、自分の状態と食い違うときに /me で確かめる
    const channel =
      typeof BroadcastChannel === "undefined"
        ? null
        : new BroadcastChannel(AUTH_CHANNEL_NAME);
    channelRef.current = channel;
    const onAuthMessage = (event: MessageEvent<AuthChannelMessage>) => {
      const userId = event.data?.userId ?? null;
      // 取得中の /me は知らせより前に出たものかもしれないので、一致していても
      // 捨てて取り直す。取得中でなく一致しているなら何もしない
      if (sessionRequestRef.current === null && userId === userIdRef.current)
        return;
      authGenerationRef.current += 1;
      sessionRequestRef.current = null;
      const generation = authGenerationRef.current;
      void loadCurrentSession().finally(() => {
        if (generation === authGenerationRef.current) setIsLoading(false);
      });
    };
    channel?.addEventListener("message", onAuthMessage);

    // Auth cookies are HttpOnly, so focus/visibility is the safe signal to
    // discover a login or logout performed in another browser or by the
    // server. This deliberately remains active after an anonymous bootstrap;
    // only the page-load request is skipped.
    window.addEventListener("focus", syncAfterPossibleCrossTabChange);
    document.addEventListener(
      "visibilitychange",
      syncAfterPossibleCrossTabChange,
    );
    return () => {
      channel?.removeEventListener("message", onAuthMessage);
      channel?.close();
      if (channelRef.current === channel) channelRef.current = null;
      window.removeEventListener("focus", syncAfterPossibleCrossTabChange);
      document.removeEventListener(
        "visibilitychange",
        syncAfterPossibleCrossTabChange,
      );
    };
  }, [bootstrapStatus, loadCurrentSession]);

  // 認証状態をリフレッシュする関数（必要時のみ）
  const refreshAuth = useCallback(
    () => loadCurrentSession(),
    [loadCurrentSession],
  );

  const updateUser = useCallback(
    (nextUser: User | null) => {
      authGenerationRef.current += 1;
      sessionRequestRef.current = null;
      publishUser(nextUser);
      setIsLoading(false);
      announceUser(nextUser?.id ?? null);
    },
    [announceUser, publishUser],
  );

  const logout = async () => {
    try {
      // Better Authのサインアウトエンドポイントを呼び出し
      await apiProxyRequest.post(null, AUTH_ENDPOINTS.SIGN_OUT, null);
      logger.info("user signed out", {
        ...LOG_CONTEXT,
        userId: user?.id,
      });
      updateUser(null);
    } catch (error) {
      logger.error("logout error", {
        ...LOG_CONTEXT,
        error,
      });
      updateUser(null);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        setUser: updateUser,
        refreshAuth,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * 本人をまだ読んでいる間 true。AuthProvider の外 (試験や単体の表示) では false。
 * 帯のように、ログイン前の姿を出してよいかを決めるだけの画面が、provider を要求せずに読む
 */
export function useAuthPending(): boolean {
  return useContext(AuthContext)?.isLoading ?? false;
}
